import type { AuthContextResponse } from '@passage/contracts';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { lazy, StrictMode, Suspense, type ComponentType, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, Navigate, Outlet } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { ApiRequestError, get } from './api.ts';
import { Shell } from './components/Shell.tsx';
import { ErrorState, Loading } from './components/ui.tsx';
import { DemoBanner, DemoNotice } from './components/demo.tsx'; // PD-729 / PD-730 demo mode
import { I18nProvider } from './i18n.tsx';
import { NAV } from './nav.ts';
import { DeniedPage, HomePage, NotFoundPage, PlannedPage } from './pages/Home.tsx';
import { ActivatePage, RecoveryPage, SignInPage } from './pages/SignIn.tsx';
import { SsoFinishPage } from './pages/SsoSignIn.tsx';
import { OrganisationPage, OrganisationsPage, PlatformAccountPage, PlatformPolicyPage, PlatformShell, usePlatformSession } from './platform/PlatformApp.tsx';
import { PlatformEmailPage } from './platform/PlatformEmail.tsx'; // PB-13-04 (email stream)
import { PlatformSettingsPage } from './platform/PlatformSettings.tsx'; // PD-710 (settings stream)
import { SessionProvider, useSession, useSessionQuery } from './session.tsx';
import './styles/app.css';

// Workspace pages load on demand so sign-in and the role home stay small.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function page<M extends Record<string, unknown>, K extends keyof M>(load: () => Promise<M>, name: K): ComponentType<any> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return lazy(() => load().then((m) => ({ default: m[name] as ComponentType<any> })));
}
const OrgAccountPage = page(() => import('./pages/Account.tsx'), 'OrgAccountPage');
const AuditPage = page(() => import('./pages/Audit.tsx'), 'AuditPage');
const SitePage = page(() => import('./pages/Sites.tsx'), 'SitePage');
const SitesIndexPage = page(() => import('./pages/Sites.tsx'), 'SitesIndexPage');
const UsersPage = page(() => import('./pages/Users.tsx'), 'UsersPage');
const UserDetailPage = page(() => import('./pages/Users.tsx'), 'UserDetailPage');
const ApprovalsPage = page(() => import('./pages/Approvals.tsx'), 'ApprovalsPage');
const HostsPage = page(() => import('./pages/People.tsx'), 'HostsPage');
const PersonPage = page(() => import('./pages/People.tsx'), 'PersonPage');
const PoliciesPage = page(() => import('./pages/Policies.tsx'), 'PoliciesPage');
const VisitFormPage = page(() => import('./pages/VisitForm.tsx'), 'VisitFormPage');
const VisitsPage = page(() => import('./pages/Visits.tsx'), 'VisitsPage');
const VisitDetailPage = page(() => import('./pages/Visits.tsx'), 'VisitDetailPage');
const VisitSeriesPage = page(() => import('./pages/VisitSeries.tsx'), 'VisitSeriesPage'); // PB-04-02
const VisitorPassPage = page(() => import('./pages/VisitorPass.tsx'), 'VisitorPassPage');
const KioskPage = page(() => import('./pages/Kiosk.tsx'), 'KioskPage');
const CommunicationsPage = page(() => import('./pages/Communications.tsx'), 'CommunicationsPage');
// Module 09 (contractors stream, ADR-013)
const ContractorsPage = page(() => import('./pages/Contractors.tsx'), 'ContractorsPage');
const DeliveriesPage = page(() => import('./pages/Deliveries.tsx'), 'DeliveriesPage');
// Modules 14/15 (safety & reporting stream, ADR-019)
const SafetyPage = page(() => import('./pages/Safety.tsx'), 'SafetyPage');
const ReportsPage = page(() => import('./pages/Reports.tsx'), 'ReportsPage');
// Module 08 (verification stream, ADR-017)
const VerificationPage = page(() => import('./pages/Verification.tsx'), 'VerificationPage');
const VerifyReturnPage = page(() => import('./components/identity-visitor.tsx'), 'VerifyReturnPage');
const ReceptionPage = page(() => import('./pages/Reception.tsx'), 'ReceptionPage');
const CardsPage = page(() => import('./pages/Cards.tsx'), 'CardsPage');
const OperationsPage = page(() => import('./pages/Operations.tsx'), 'OperationsPage');
const SiteBatchPage = page(() => import('./pages/SiteBatch.tsx'), 'SiteBatchPage'); // ADR-029
const IdentityPage = page(() => import('./pages/Identity.tsx'), 'IdentityPage');
const ProviderPage = page(() => import('./pages/Identity.tsx'), 'ProviderPage');
const TestResultPage = page(() => import('./pages/Identity.tsx'), 'TestResultPage');
const RolePreviewPage = page(() => import('./pages/Identity.tsx'), 'RolePreviewPage');
// Modules 16 and 18 (privacy & integrations stream, ADR-020)
const PrivacyPage = page(() => import('./pages/Privacy.tsx'), 'PrivacyPage');
const IntegrationsPage = page(() => import('./pages/Integrations.tsx'), 'IntegrationsPage');
const VehiclesPage = page(() => import('./pages/Vehicles.tsx'), 'VehiclesPage');
// Module 20 (AI add-ons stream, ADR-024)
const AiPage = page(() => import('./pages/Ai.tsx'), 'AiPage');
// Calendars stream (ADR-022)
const CalendarsPage = page(() => import('./pages/Calendars.tsx'), 'CalendarsPage');
const CalendarConnectionPage = page(() => import('./pages/Calendars.tsx'), 'CalendarConnectionPage');
const PreRegistrationLinkPage = page(() => import('./pages/PreRegistrationLink.tsx'), 'PreRegistrationLinkPage');
// OR-10 configuration dictionary (PD-055, PD-056)
const ConfigurationPage = page(() => import('./pages/Configuration.tsx'), 'ConfigurationPage');
const ConfigPromotionPage = page(() => import('./pages/ConfigPromotion.tsx'), 'ConfigPromotionPage'); // PB-19-09 (opsview stream)
// People & records stream (ADR-023)
const ScreeningPage = page(() => import('./pages/Screening.tsx'), 'ScreeningPage');
const HostSyncPage = page(() => import('./pages/PeopleRecords.tsx'), 'HostSyncPage');
const DepartmentsPage = page(() => import('./pages/Departments.tsx'), 'DepartmentsPage'); // PB-18-07
const HrMappingPage = page(() => import('./pages/Departments.tsx'), 'HrMappingPage'); // PB-18-07
const ImportPage = page(() => import('./pages/PeopleRecords.tsx'), 'ImportPage');
const FieldDictionaryPage = page(() => import('./pages/PeopleRecords.tsx'), 'FieldDictionaryPage');
// People & organisations stream (ADR-028)
const PeopleOrgsPage = page(() => import('./pages/Organisations.tsx'), 'PeopleOrgsPage');
const OrganisationDetailPage = page(() => import('./pages/Organisations.tsx'), 'OrganisationPage');
const ReassignmentsPage = page(() => import('./pages/Organisations.tsx'), 'ReassignmentsPage');

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Never retry authorization or not-found answers; they are facts, not transient failures.
      retry: (count, err) => !(err instanceof ApiRequestError && err.status >= 400 && err.status < 500) && count < 2,
      refetchOnWindowFocus: true,
    },
  },
});

function Authenticated() {
  const q = useSessionQuery();
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (!q.data) return <Navigate to="/sign-in" replace />;
  return (
    <SessionProvider session={q.data}>
      <Shell>
        <Suspense fallback={<Loading />}>
          <Outlet />
        </Suspense>
      </Shell>
    </SessionProvider>
  );
}

function PlatformAuthenticated() {
  const q = usePlatformSession();
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (!q.data) return <Navigate to="/sign-in" replace />;
  return (
    <PlatformShell>
      <Outlet />
    </PlatformShell>
  );
}

/** UX guard only: shows an explanation instead of a page the API would refuse anyway. */
function RequireAny({ actions, children }: { actions: readonly string[]; children: ReactNode }) {
  const { canAnywhere } = useSession();
  return actions.some((a) => canAnywhere(a)) ? <>{children}</> : <DeniedPage />;
}

const planned = Object.values(NAV)
  .filter((n) => n.milestone)
  .map((n) => ({ path: n.route, element: <PlannedPage route={n.route} /> }));

// Created lazily: only the router for this host's surface may subscribe to browser history.
let organisationRouter: ReturnType<typeof createBrowserRouter> | null = null;
const getOrganisationRouter = () => (organisationRouter ??= createBrowserRouter([
  { path: '/sign-in', element: <SignInPage /> },
  { path: '/account-recovery', element: <RecoveryPage /> },
  { path: '/activate', element: <ActivatePage /> },
  { path: '/sign-in/sso', element: <SsoFinishPage /> },
  // Invitation-scoped visitor pass: no staff session or chrome (PB-05-01).
  { path: '/i/:token', element: <Suspense fallback={<Loading />}><VisitorPassPage /></Suspense> },
  // PB-04-10: purpose-limited pre-registration link (checklist only, no pass).
  { path: '/r/:token', element: <Suspense fallback={<Loading />}><PreRegistrationLinkPage /></Suspense> },
  { path: '/kiosk', element: <Suspense fallback={<Loading />}><KioskPage /></Suspense> },
  // UAE PASS return (PB-05-06): no staff session; the browser-bound transaction authorises it.
  { path: '/verify/return', element: <Suspense fallback={<Loading />}><VerifyReturnPage /></Suspense> },
  {
    path: '/',
    element: <Authenticated />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'sites', element: <SitesIndexPage /> },
      { path: 'sites/configuration', element: <RequireAny actions={['tenant.settings.read', 'site.update']}><ConfigurationPage /></RequireAny> },
      { path: 'sites/batch', element: <RequireAny actions={['site.batch.update']}><SiteBatchPage /></RequireAny> },
      { path: 'sites/promotion', element: <RequireAny actions={['config.bundle.read']}><ConfigPromotionPage /></RequireAny> },
      { path: 'sites/:siteId', element: <SitePage tab="locations" /> },
      { path: 'sites/:siteId/settings', element: <SitePage tab="settings" /> },
      { path: 'sites/:siteId/branding', element: <SitePage tab="branding" /> },
      { path: 'sites/:siteId/kiosks', element: <SitePage tab="kiosks" /> },
      { path: 'sites/:siteId/room-calendars', element: <SitePage tab="room-calendars" /> },
      { path: 'sites/:siteId/hours', element: <SitePage tab="hours" /> },
      { path: 'sites/:siteId/guidance', element: <SitePage tab="guidance" /> },
      { path: 'users', element: <RequireAny actions={['user.read', 'user.directory.read']}><UsersPage /></RequireAny> },
      { path: 'users/:userId', element: <RequireAny actions={['user.read', 'user.directory.read']}><UserDetailPage /></RequireAny> },
      { path: 'auditlog', element: <RequireAny actions={['audit.read']}><AuditPage /></RequireAny> },
      { path: 'users/hosts', element: <RequireAny actions={['host.manage']}><HostsPage /></RequireAny> },
      { path: 'users/identity', element: <RequireAny actions={['identity.read']}><IdentityPage /></RequireAny> },
      { path: 'users/identity/providers/:providerId', element: <RequireAny actions={['identity.read']}><ProviderPage /></RequireAny> },
      { path: 'users/identity/test', element: <RequireAny actions={['identity.provider.manage']}><TestResultPage /></RequireAny> },
      { path: 'users/roles', element: <RequireAny actions={['grant.read', 'user.directory.read']}><RolePreviewPage /></RequireAny> },
      { path: 'users/calendars', element: <RequireAny actions={['calendar.read']}><CalendarsPage /></RequireAny> },
      { path: 'users/calendars/:connectionId', element: <RequireAny actions={['calendar.read']}><CalendarConnectionPage /></RequireAny> },
      { path: 'users/host-sync', element: <RequireAny actions={['host.sync.manage']}><HostSyncPage /></RequireAny> },
      { path: 'users/departments', element: <RequireAny actions={['host.manage', 'host.sync.manage']}><DepartmentsPage /></RequireAny> },
      { path: 'users/hr-mapping', element: <RequireAny actions={['host.sync.manage']}><HrMappingPage /></RequireAny> },
      { path: 'users/import', element: <RequireAny actions={['record.import']}><ImportPage /></RequireAny> },
      { path: 'users/fields', element: <RequireAny actions={['field.dictionary.manage']}><FieldDictionaryPage /></RequireAny> },
      { path: 'screening', element: <RequireAny actions={['screening.case.review', 'screening.list.manage']}><ScreeningPage /></RequireAny> },
      { path: 'visits', element: <RequireAny actions={['visit.read.own', 'visit.read.site', 'visit.read.delegated']}><VisitsPage /></RequireAny> },
      { path: 'visits/new', element: <RequireAny actions={['visit.create']}><VisitFormPage mode="new" /></RequireAny> },
      { path: 'visits/:visitId', element: <VisitDetailPage /> },
      { path: 'visits/:visitId/edit', element: <VisitFormPage mode="edit" /> },
      { path: 'visit-series/:seriesId', element: <VisitSeriesPage /> },
      { path: 'approvals', element: <RequireAny actions={['approval.read.assigned', 'approval.reassign']}><ApprovalsPage /></RequireAny> },
      { path: 'policies', element: <RequireAny actions={['policy.read', 'policy.draft']}><PoliciesPage /></RequireAny> },
      { path: 'people', element: <RequireAny actions={['organisation.read', 'organisation.manage']}><PeopleOrgsPage /></RequireAny> },
      { path: 'people/organisations/:orgId', element: <RequireAny actions={['organisation.read', 'organisation.manage']}><OrganisationDetailPage /></RequireAny> },
      { path: 'people/reassignments', element: <RequireAny actions={['host.manage', 'visit.read.delegated']}><ReassignmentsPage /></RequireAny> },
      { path: 'people/:personId', element: <PersonPage /> },
      { path: 'reception', element: <RequireAny actions={['reception.queue.read']}><ReceptionPage /></RequireAny> },
      { path: 'operations', element: <RequireAny actions={['integration.read']}><OperationsPage /></RequireAny> },
      { path: 'credentials', element: <RequireAny actions={['custody.assign', 'card.manage', 'credential.reconcile', 'integration.read', 'credential.read', 'credential.issue', 'badge.template.manage']}><CardsPage /></RequireAny> },
      { path: 'vehicles', element: <RequireAny actions={['gate.review', 'vehicle.manage', 'parking.manage', 'parking.allocate']}><VehiclesPage /></RequireAny> },
      { path: 'communications', element: <RequireAny actions={['template.manage', 'message.read']}><CommunicationsPage /></RequireAny> },
      { path: 'account', element: <OrgAccountPage /> },
      { path: 'contractors', element: <RequireAny actions={['contractor.read', 'contractor.requirements.manage']}><ContractorsPage /></RequireAny> },
      { path: 'identity', element: <RequireAny actions={['identity.review', 'verification.capture', 'verification.settings.manage', 'verification.provider.manage']}><VerificationPage /></RequireAny> },
      { path: 'deliveries', element: <RequireAny actions={['delivery.read', 'delivery.read.own', 'delivery.point.manage']}><DeliveriesPage /></RequireAny> },
      { path: 'safety', element: <RequireAny actions={['muster.read', 'reception.queue.read', 'integration.read', 'site.update']}><SafetyPage /></RequireAny> },
      { path: 'summary', element: <RequireAny actions={['report.aggregate.read']}><ReportsPage /></RequireAny> },
      { path: 'privacy', element: <RequireAny actions={['privacy.request.read', 'retention.read', 'retention.manage', 'privacy.hold.manage', 'privacy.purge.plan', 'privacy.hold.release']}><PrivacyPage /></RequireAny> },
      { path: 'ai', element: <RequireAny actions={['ai.reporting.use', 'ai.decision_support.use', 'ai.settings.read', 'ai.settings.manage', 'ai.activity.read']}><AiPage /></RequireAny> },
      { path: 'integrations', element: <RequireAny actions={['integration.read', 'integration.org.read']}><IntegrationsPage /></RequireAny> },
      ...planned,
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]));

let platformRouter: ReturnType<typeof createBrowserRouter> | null = null;
const getPlatformRouter = () => (platformRouter ??= createBrowserRouter([
  { path: '/sign-in', element: <SignInPage surface="platform" /> },
  { path: '/activate', element: <ActivatePage surface="platform" /> },
  {
    path: '/',
    element: <PlatformAuthenticated />,
    children: [
      { index: true, element: <OrganisationsPage /> },
      { path: 'organisations/:tenantId', element: <OrganisationPage /> },
      { path: 'policy', element: <PlatformPolicyPage /> },
      { path: 'email', element: <PlatformEmailPage /> }, // PB-13-04 (PD-580)
      { path: 'settings', element: <PlatformSettingsPage /> }, // PD-710 installation settings
      { path: 'account', element: <PlatformAccountPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]));

/** Chooses the surface from the host (PD-003). Language binding lives in the session providers (PD-011). */
function Root() {
  const ctx = useQuery({ queryKey: ['auth-context'], queryFn: () => get<AuthContextResponse>('/api/v1/auth/context'), staleTime: Infinity });
  if (ctx.isPending) return null;
  return (
    <I18nProvider demo={ctx.data?.demo === true}>
      {/* PD-729: demo mode (IS_DEMO) — one banner and one refusal notice for every surface and page. */}
      {ctx.data?.demo ? <DemoBanner /> : null}
      {ctx.isError ? <ErrorState error={ctx.error} retry={() => void ctx.refetch()} /> : <RouterProvider router={ctx.data.surface === 'platform' ? getPlatformRouter() : getOrganisationRouter()} />}
      {ctx.data?.demo ? <DemoNotice /> : null}
    </I18nProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <Root />
    </QueryClientProvider>
  </StrictMode>,
);
